import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Eye, X, ChevronDown, Crown, UserCog, Users, Handshake } from 'lucide-react';

const ROLES = [
  { key: 'admin', label: 'Admin', desc: 'Your full access', icon: Crown, tone: 'text-red-400' },
  { key: 'manager', label: 'Recruiting Manager', desc: 'Team lead view', icon: UserCog, tone: 'text-purple-400' },
  { key: 'recruiter', label: 'Recruiter', desc: 'Sourcing & ATS', icon: Users, tone: 'text-violet-400' },
  { key: 'bde', label: 'BDE', desc: 'Business dev', icon: Handshake, tone: 'text-amber-400' },
];

const roleLabel = (r) => (r === 'bde' ? 'BDE' : r ? r.charAt(0).toUpperCase() + r.slice(1) : '');

/* Sidebar dropdown — only rendered for real admins. Lets the admin preview
   the app as another role (UI/UX only; token stays admin). */
export const ViewAsSwitcher = ({ onNavigate }) => {
  const { realUser, viewAsRole, setViewAs } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (realUser?.role !== 'admin') return null;

  const current = viewAsRole || 'admin';

  const pick = (role) => {
    setOpen(false);
    onNavigate?.();
    setViewAs(role === 'admin' ? null : role);
    // Land on a role-appropriate page (guards auto-route BDE to business-dev)
    navigate('/app');
  };

  return (
    <div className="relative px-1 pt-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04] transition-all text-left"
        data-testid="view-as-switcher-btn"
      >
        <Eye className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 leading-none">View as</p>
          <p className="text-xs font-medium text-white truncate leading-tight mt-1">{roleLabel(current)}</p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-1 right-1 mb-1 bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150 z-50">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = current === r.key;
            return (
              <button
                key={r.key}
                onClick={() => pick(r.key)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${active ? 'bg-white/[0.05]' : 'hover:bg-white/[0.04]'}`}
                data-testid={`view-as-${r.key}`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${r.tone}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white leading-tight">{r.label}</p>
                  <p className="text-xs text-slate-500 leading-tight">{r.desc}</p>
                </div>
                {active && <span className="text-[11px] uppercase tracking-wider text-indigo-400 font-semibold">Current</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* Persistent banner shown across the top while an admin is previewing a role. */
export const ViewAsBanner = () => {
  const { realUser, viewAsRole, setViewAs } = useAuth();
  const navigate = useNavigate();

  if (realUser?.role !== 'admin' || !viewAsRole || viewAsRole === 'admin') return null;

  const exit = () => {
    setViewAs(null);
    navigate('/app/dashboard');
  };

  return (
    <div
      className="sticky top-0 z-[60] flex items-center justify-center gap-3 px-4 py-2 bg-indigo-600 text-white text-xs md:text-sm shadow-lg"
      data-testid="view-as-banner"
    >
      <Eye className="w-4 h-4 shrink-0" />
      <span className="font-medium">
        Viewing as <span className="font-bold underline underline-offset-2">{roleLabel(viewAsRole)}</span>
        <span className="hidden sm:inline text-indigo-200 font-normal"> — this is exactly what a {roleLabel(viewAsRole)} sees</span>
      </span>
      <button
        onClick={exit}
        className="ml-1 flex items-center gap-1 px-2 py-1 rounded-md bg-white/15 hover:bg-white/25 transition-colors font-semibold"
        data-testid="view-as-exit-btn"
      >
        <X className="w-3.5 h-3.5" /> Exit to Admin
      </button>
    </div>
  );
};
